import { Component, OnInit } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { PolicyService } from '../services/policy.service';
import { PolicyDetails } from '../models/Policy';
import { Currency } from '../../currencies/models/Currency';
import { CurrenciesService } from '../../currencies/services/currencies.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';

@Component({
  selector: 'app-policy-info-form',
  templateUrl: './policy-info-form.component.html',
  styleUrls: ['./policy-info-form.component.scss'],
  providers: [ CurrenciesService, CurrencyPipe ],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatIcon,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOption,
    MatRadioModule,
    MatSelectModule,
    MatExpansionModule,
    RouterModule
  ]
})
export class PolicyInfoFormComponent implements OnInit {

  loading = new BehaviorSubject<boolean>(false);
  policyInfoForm!: FormGroup;
  currencies!: Currency[];
  policy!: PolicyDetails;
  policyId: any;
  allPolicies: any[] = [];
  policyNameExists: boolean = false;
  existingPolicy: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private policyService: PolicyService,
    private currenciesService: CurrenciesService,
    private logService: Logger,
    private router: Router,
    private route: ActivatedRoute,
    public userContextProvider: UserContextProvider
  ) { }

  ngOnInit() {
    this.policyId = this.getPolicyIdFromUrl();
    this.loadPolicyInfoFormData();
    this.initPolicyInfoForm();
  }

  async loadPolicyInfoFormData() {
    try {
      this.loading.next(true);

      const currenciesResponse = await this.currenciesService.getCurrencies();
      this.currencies = currenciesResponse;

      this.allPolicies = await this.policyService.getPolicies();

      if (this.policyId) {
        this.policyService.getPolicyDetail(this.policyId).then(async(policy: PolicyDetails) => {
          this.loadFromPolicy(policy);
          this.policy = policy;
          this.existingPolicy = true;
        }).finally(() => {
          if (this.loadingComplete()) {
            this.loading.next(false);
          }
        });
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Policy form', 'Error while retrieving Policy form');
    } finally {
      this.loading.next(false);
    }
  }

  async loadFromPolicy(policy: PolicyDetails) {
    this.policyInfoForm.patchValue(policy);
    this.policyInfoForm.controls['currency_id'].setValue(policy.currency_id.toString());
    this.policyInfoForm.controls['all_countries'].setValue(policy.all_countries.toString());

    this.policyInfoForm.markAllAsTouched();
    this.policyInfoForm.markAsDirty();
  }

  initPolicyInfoForm() {
    this.policyInfoForm = this.formBuilder.group({
      name: ['', {
        validators: [Validators.required, this.checkForDuplicatePolicyName()],
        updateOn: 'change'
      }],
      currency_id: ['', Validators.required],
      all_countries: [''],
      range_reservation_price_from: [''],
      range_reservation_price_to: [''],
      range_guests_from: [''],
      range_guests_to: [''],
      range_no_of_bedrooms_from: [''],
      range_no_of_bedrooms_to: [''],
      dwelling_coverage: [''],
      liability_coverage: [''],
      damage_coverage: [''],
      description: [''],
      note: ['']
    });
  }

  checkForDuplicatePolicyName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.allPolicies.some(policy => policy.name.toLowerCase() === control.value.toLowerCase() && policy.id !== Number(this.policyId))) {
        this.policyNameExists = true;
      } else {
        this.policyNameExists = false;
      }
      return this.policyNameExists ? { invalidPolicyName: this.policyNameExists } : null;
    };
  }

  loadingComplete() {
    let currenciesLoaded: boolean = this.currencies !== undefined && this.currencies !== null && this.currencies.length > 0;
    return currenciesLoaded;
  }

  async submitPolicyInfo() {
    this.loading.next(true);

    try {
      let policyInfo = await this.buildPolicyInfo();

      if (this.existingPolicy) {
        this.policyId = (await this.policyService.updatePolicy(this.policyId, policyInfo)).id;
      } else {
        const policyId = (await this.policyService.createPolicy(policyInfo)).id;
        this.policyId = policyId;
        this.existingPolicy = true;
      }

      if (!this.policyId) {
        this.logService.handleError('Error while submitting Policy - Policy ID does not exist', 'An error occurred while submitting Policy', 'Error while submitting Policy');
      } else {
        this.navigateToPolicyDetailPage(this.policyId);
      }
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while submitting Policy', 'Error while submitting Policy');
    } finally {
      this.loading.next(false);
    }
  }

  async buildPolicyInfo() {
    try {
      let policyInfoPayload: any = {
        ...this.policyInfoForm.value
      }

      return policyInfoPayload;
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Policy', 'Error while submitting Policy');
      return null;
    }
  }

  getPolicyIdFromUrl() {
    return this.route.snapshot.queryParamMap.get('id');
  }

  canCreatePolicyInfo(): boolean {
    return (
      this.policyInfoForm.valid &&
      !this.policyNameExists
    );
  }

  navigateToPolicyDetailPage(policyId: string) {
    this.router.navigate([`/policies/detail/${policyId}`]);
  }
}